import React, { PropsWithChildren, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import cx from "classnames";
import { useMediaQuery } from "react-responsive";
import * as Popover from "@radix-ui/react-popover";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ChatSettingsV2,
  SvgRadius,
  ChatPosition,
  isChatPosition,
} from "../entities/ChatSettingsV2";
import { QuackChatWidgetTheme } from "./QuackChatWidgetTheme";
import { WidgetSVG } from "./loadSVG";
import { useCustomAnchor } from "../hooks/useCustomAnchor";
import { Badge } from "@radix-ui/themes";

function useChatPositionOverrides(defaultPosition: ChatPosition) {
  const [position, setPosition] = useState<ChatPosition>(defaultPosition);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === "QUACK_ALIGN_CHAT" && isChatPosition(event.data.position)) {
        setPosition(event.data.position);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  return position;
}

function CustomAnchor({ element }: { element: HTMLElement }) {
  return createPortal(
    <Popover.Anchor asChild>
      <button className="qc-Chat__Custom_Anchor" aria-label="Chat Support" />
    </Popover.Anchor>,
    element,
  );
}

function NotificationCount({ notificationCount }: { notificationCount?: number }) {
  const show = notificationCount != null && notificationCount > 0;

  return (
    <Badge
      variant="surface"
      color="blue"
      className="qc-Chat__Notification_Count"
      radius="full"
      highContrast
      style={{ display: show ? "block" : "none" }}
    >
      {show ? notificationCount : ""}
    </Badge>
  );
}

interface TriggerSVGProps {
  open: boolean;
  customSVG: string;
  closeSVG?: string;
  iconSize: number;
  radius?: SvgRadius;
  notificationCount?: number;
}

export function TriggerSVG({
  open,
  customSVG,
  closeSVG,
  iconSize,
  radius,
  notificationCount,
}: TriggerSVGProps) {
  if (closeSVG == null) {
    return (
      <>
        <WidgetSVG path={customSVG} iconSize={iconSize} />
        <NotificationCount notificationCount={notificationCount} />
      </>
    );
  }

  return (
    <>
      <WidgetSVG path={customSVG} iconSize={iconSize} hide={open} radius={radius} />
      <WidgetSVG path={closeSVG} iconSize={iconSize} hide={!open} radius={radius} />
      <NotificationCount notificationCount={notificationCount} />
    </>
  );
}

interface QuackWidgetContainerProps {
  open: boolean;
  settings: ChatSettingsV2;
  notificationCount?: number;
  onOpenChange: (open: boolean) => void;
  className?: string;
}

function SmallScreenContainer({
  children,
  open,
  notificationCount,
  onOpenChange,
  settings,
  className,
}: PropsWithChildren<QuackWidgetContainerProps>) {
  const {
    customSVG,
    closeSVG = customSVG,
    iconSize,
    position = "bottom-right",
    svgRadius,
    theme,
    customAnchor,
    name,
  } = settings;

  const { element, error } = useCustomAnchor(customAnchor);

  const triggerClassName = cx(
    "quackchat-root",
    "qc-Chat__Trigger",
    position,
    `radius-${svgRadius}`,
  );

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {customAnchor != null ? (
        <>
          {element == null && error != null && (
            <Dialog.Trigger asChild>
              <button className={triggerClassName} aria-label="Chat Support">
                <TriggerSVG
                  open={open}
                  customSVG={customSVG}
                  closeSVG={closeSVG}
                  iconSize={iconSize}
                  radius={svgRadius}
                  notificationCount={notificationCount}
                />
              </button>
            </Dialog.Trigger>
          )}
        </>
      ) : (
        <Dialog.Trigger asChild>
          <button className={triggerClassName} aria-label="Chat Support">
            <TriggerSVG
              open={open}
              customSVG={customSVG}
              closeSVG={closeSVG}
              iconSize={iconSize}
              radius={svgRadius}
              notificationCount={notificationCount}
            />
          </button>
        </Dialog.Trigger>
      )}

      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content aria-label={`${name} Chat`} aria-modal="true">
          <QuackChatWidgetTheme themeVars={theme}>
            <div className={cx("qc-Chat__Widget_Dialog", className)}>{children}</div>
          </QuackChatWidgetTheme>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function QuackWidgetContainer({
  children,
  open,
  onOpenChange,
  settings,
  className,
  notificationCount,
}: PropsWithChildren<QuackWidgetContainerProps>) {
  const { customSVG, closeSVG = customSVG, iconSize, svgRadius, customAnchor, name } = settings;

  const { element, error } = useCustomAnchor(customAnchor);
  const isSmallScreen = useMediaQuery({ query: "(max-width: 520px)" });
  const position = useChatPositionOverrides(settings.position ?? "bottom-right");

  if (isSmallScreen) {
    return (
      <SmallScreenContainer
        open={open}
        onOpenChange={onOpenChange}
        settings={settings}
        notificationCount={notificationCount}
        className={className}
      >
        {children}
      </SmallScreenContainer>
    );
  }

  const triggerClassName = cx(
    "quackchat-root",
    "qc-Chat__Trigger",
    position,
    `radius-${svgRadius}`,
  );

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      {customAnchor != null ? (
        <>
          {element != null && <CustomAnchor element={element} />}

          {element == null && error != null && (
            <Popover.Trigger asChild>
              <button className={triggerClassName} aria-label="Chat Support">
                <TriggerSVG
                  open={open}
                  customSVG={customSVG}
                  closeSVG={closeSVG}
                  iconSize={iconSize}
                  radius={svgRadius}
                  notificationCount={notificationCount}
                />
              </button>
            </Popover.Trigger>
          )}
        </>
      ) : (
        <Popover.Trigger asChild>
          <button className={triggerClassName} aria-label="Chat Support">
            <TriggerSVG
              open={open}
              customSVG={customSVG}
              closeSVG={closeSVG}
              iconSize={iconSize}
              radius={svgRadius}
              notificationCount={notificationCount}
            />
          </button>
        </Popover.Trigger>
      )}

      <Popover.Content
        style={{ zIndex: 10000000 }}
        align={position === "bottom-right" ? "end" : "start"}
        side={"top"}
        sideOffset={8}
        className={cx("qc-Chat__Widget_Popover", className)}
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
        sticky={"partial"}
        aria-label={`${name} Chat`}
      >
        {children}
      </Popover.Content>
    </Popover.Root>
  );
}
